import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "./context/AppContext";

const GAME_TABS = [
  { key: "presidents", label: "US PRESIDENTS", color: "#00d4ff", icon: "🎩" },
  { key: "states",     label: "US STATES",     color: "#39ff14", icon: "🗺️" },
  { key: "leaders",    label: "WORLD LEADERS", color: "#bf00ff", icon: "👑" },
];

const MARQUEE_TEXT =
  "★ AI ARCADE ★ GUESS THE PRESIDENT ★ GUESS THE STATE ★ GUESS THE LEADER ★ " +
  "TOP PLAYERS RULE THE ARCADE ★ CAN YOU MAKE IT ON THE BOARD? ★ ";

export default function HighScoreScreen() {
  const navigate = useNavigate();
  const { player } = useContext(AppContext);
  const [activeGame, setActiveGame] = useState("presidents");
  const [scoreCache, setScoreCache] = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const fetchScores = async (gameType) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/chat/score/leaderboard`,
        { gameType, limit: 10 },
        { headers: { "Content-Type": "application/json" } }
      );
      const raw =
        typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body ?? res.data;
      const list = raw?.scores ?? raw?.items ?? (Array.isArray(raw) ? raw : []);
      setScoreCache((prev) => ({ ...prev, [gameType]: list }));
    } catch {
      setError("TRANSMISSION ERROR — SCORES UNAVAILABLE");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchScores(activeGame);
  }, [activeGame]);

  const tab        = GAME_TABS.find((t) => t.key === activeGame);
  const list       = scoreCache[activeGame] || [];
  const accentColor = tab.color;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes hs-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes hs-glow {
          0%, 100% { text-shadow: 0 0 10px #ff0, 0 0 30px #ff0, 0 0 60px #ff0; }
          50%       { text-shadow: 0 0 4px  #ff0, 0 0 12px #ff0, 0 0 24px #ff0; }
        }
        @keyframes hs-marquee {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes hs-scan {
          0%   { top: -8px; }
          100% { top: 100%; }
        }
        @keyframes hs-fadein {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .hs-row { animation: hs-fadein 0.3s ease both; }
      `}</style>

      {/* CRT scanlines */}
      <div style={styles.scanlines} />
      {/* Moving scan highlight */}
      <div style={styles.scanBeam} />

      <div style={styles.cabinet}>

        {/* ── Title ─────────────────────────────────── */}
        <div style={styles.titleBlock}>
          <div style={styles.titleDecor}>
            {"■ ".repeat(14)}
          </div>
          <h1 style={styles.title}>HIGH SCORES</h1>
          <div style={styles.titleDecor}>
            {" ■".repeat(14)}
          </div>
          <div style={styles.insertCoin}>
            — INSERT COIN TO PLAY —
          </div>
        </div>

        {/* ── Game tabs ─────────────────────────────── */}
        <div style={styles.tabs}>
          {GAME_TABS.map((t) => {
            const active = t.key === activeGame;
            return (
              <button
                key={t.key}
                style={{
                  ...styles.tab,
                  borderColor: active ? t.color : "#333",
                  color:       active ? t.color : "#555",
                  textShadow:  active ? `0 0 8px ${t.color}, 0 0 20px ${t.color}` : "none",
                  boxShadow:   active
                    ? `0 0 8px ${t.color}44, inset 0 0 12px ${t.color}22`
                    : "none",
                  background: active ? "#0a0a0a" : "transparent",
                }}
                onClick={() => setActiveGame(t.key)}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Score table ───────────────────────────── */}
        <div style={{ ...styles.board, borderColor: accentColor, boxShadow: `0 0 16px ${accentColor}44` }}>
          {/* Column headers */}
          <div style={styles.colHeader}>
            <span style={{ ...styles.colRank, color: accentColor }}>RNK</span>
            <span style={{ ...styles.colName, color: accentColor }}>NAME</span>
            <span style={{ ...styles.colScore, color: accentColor }}>SCORE</span>
          </div>

          <div style={{ borderBottom: `1px solid ${accentColor}44`, marginBottom: 6 }} />

          {loading && (
            <div style={styles.statusMsg}>
              LOADING<span style={{ animation: "hs-blink 0.6s step-start infinite" }}>…</span>
            </div>
          )}

          {!loading && error && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ ...styles.statusMsg, color: "#ff3333", animation: "hs-blink 1s step-start infinite", padding: 0 }}>
                {error}
              </div>
              <button style={styles.retryBtn} onClick={() => fetchScores(activeGame)}>
                ↺ RETRY
              </button>
            </div>
          )}

          {!loading && !error && list.length === 0 && (
            <div style={styles.statusMsg}>NO SCORES YET — BE THE FIRST!</div>
          )}

          {!loading && !error && list.map((entry, i) => {
            const name  = entry.displayName ?? entry.name ?? entry.ID ?? "UNKNOWN";
            const score = entry.score ?? entry.Score ?? entry.amount ?? 0;
            const isYou = player && (name === player);

            const rankColor =
              i === 0 ? "#ffd700"
              : i === 1 ? "#c0c0c0"
              : i === 2 ? "#cd7f32"
              : "#888";

            return (
              <div
                key={i}
                className="hs-row"
                style={{
                  ...styles.scoreRow,
                  animationDelay: `${i * 60}ms`,
                  background: isYou
                    ? "rgba(255,255,0,0.07)"
                    : i < 3
                      ? `rgba(${i === 0 ? "255,215,0" : i === 1 ? "192,192,192" : "205,127,50"},0.04)`
                      : "transparent",
                  borderLeft: `3px solid ${i < 3 ? rankColor : "transparent"}`,
                }}
              >
                <span style={{ ...styles.colRank, color: rankColor, fontWeight: 700 }}>
                  {String(i + 1).padStart(3, "0")}
                </span>
                <span style={{ ...styles.colName, color: isYou ? "#ff0" : "#e0e0e0" }}>
                  {name.toUpperCase().slice(0, 12).padEnd(12, " ")}
                  {isYou && (
                    <span style={styles.youTag}> ◄YOU</span>
                  )}
                </span>
                <span style={{ ...styles.colScore, color: i === 0 ? "#ffd700" : accentColor }}>
                  {String(score).padStart(6, "0")}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Marquee ───────────────────────────────── */}
        <div style={styles.marqueeWrap}>
          <div style={{ animation: "hs-marquee 18s linear infinite", whiteSpace: "nowrap" }}>
            {MARQUEE_TEXT}{MARQUEE_TEXT}
          </div>
        </div>

        {/* ── Back button ───────────────────────────── */}
        <div style={styles.footerRow}>
          <button style={styles.backBtn} onClick={() => navigate("/")}>
            ◄ BACK TO ARCADE
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#000",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "20px 12px",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Courier New', Courier, monospace",
  },
  scanlines: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    zIndex: 10,
    background:
      "repeating-linear-gradient(to bottom, transparent 0px, transparent 3px, rgba(0,0,0,0.35) 3px, rgba(0,0,0,0.35) 4px)",
  },
  scanBeam: {
    position: "fixed",
    left: 0,
    right: 0,
    height: 8,
    background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.03), transparent)",
    pointerEvents: "none",
    zIndex: 11,
    animation: "hs-scan 5s linear infinite",
  },
  cabinet: {
    width: "100%",
    maxWidth: 480,
    background: "#050505",
    border: "3px solid #333",
    borderRadius: 6,
    padding: "20px 16px 16px",
    boxShadow: "0 0 40px rgba(0,212,255,0.12), 0 0 80px rgba(0,0,0,0.8)",
    position: "relative",
    zIndex: 1,
  },

  /* Title */
  titleBlock: {
    textAlign: "center",
    marginBottom: 18,
  },
  titleDecor: {
    color: "#333",
    fontSize: 9,
    letterSpacing: 1,
    lineHeight: "10px",
  },
  title: {
    margin: "6px 0",
    fontSize: 32,
    fontWeight: 900,
    letterSpacing: 6,
    color: "#ff0",
    animation: "hs-glow 2s ease-in-out infinite",
    textTransform: "uppercase",
  },
  insertCoin: {
    color: "#ff0",
    fontSize: 11,
    letterSpacing: 3,
    marginTop: 4,
    animation: "hs-blink 1s step-start infinite",
  },

  /* Tabs */
  tabs: {
    display: "flex",
    gap: 6,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  tab: {
    flex: 1,
    padding: "6px 4px",
    border: "2px solid",
    borderRadius: 4,
    fontSize: 9,
    fontFamily: "'Courier New', Courier, monospace",
    fontWeight: 700,
    letterSpacing: 1.2,
    cursor: "pointer",
    textTransform: "uppercase",
    transition: "all 0.15s",
    minWidth: 80,
  },

  /* Score board */
  board: {
    border: "2px solid",
    borderRadius: 4,
    padding: "12px 10px",
    marginBottom: 12,
    minHeight: 200,
  },
  colHeader: {
    display: "flex",
    marginBottom: 4,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  colRank: {
    width: 44,
    flexShrink: 0,
    textAlign: "center",
  },
  colName: {
    flex: 1,
    paddingLeft: 10,
    fontFamily: "'Courier New', Courier, monospace",
  },
  colScore: {
    width: 72,
    textAlign: "right",
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
  },
  scoreRow: {
    display: "flex",
    alignItems: "center",
    padding: "5px 4px",
    borderRadius: 3,
    marginBottom: 3,
    fontSize: 13,
  },
  statusMsg: {
    color: "#555",
    fontSize: 12,
    textAlign: "center",
    padding: "30px 0",
    letterSpacing: 2,
  },
  youTag: {
    color: "#ff0",
    fontSize: 10,
    animation: "hs-blink 1s step-start infinite",
    fontWeight: 700,
  },

  /* Marquee */
  marqueeWrap: {
    overflow: "hidden",
    borderTop: "1px solid #222",
    borderBottom: "1px solid #222",
    padding: "6px 0",
    marginBottom: 14,
    color: "#ff0",
    fontSize: 10,
    letterSpacing: 2,
    textTransform: "uppercase",
  },

  /* Footer */
  footerRow: {
    textAlign: "center",
  },
  retryBtn: {
    marginTop: 10,
    background: "transparent",
    border: "1px solid #ff3333",
    color: "#ff3333",
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    padding: "5px 16px",
    borderRadius: 4,
    cursor: "pointer",
  },
  backBtn: {
    background: "transparent",
    border: "2px solid #444",
    color: "#888",
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 2,
    padding: "8px 24px",
    borderRadius: 4,
    cursor: "pointer",
    textTransform: "uppercase",
    transition: "all 0.15s",
  },
};
